import React from 'react';
import { Popover, Progress, Typography, Divider, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, ArrowUpRight } from 'lucide-react';
import { useAccountClaims } from '../hooks/useAccountClaims';
import { useHasAdminAccess } from '../hooks/useHasAccess';
import { accountsApi } from '../services/accountsApi';

const { Text } = Typography;

/**
 * <SeatQuotaBadge>
 *
 * Discreet header affordance shown to tenant_admin users (admin, super_admin,
 * master_admin) surfacing seat quota. Renders a subtle `Users` icon whose color
 * reflects the highest of the two utilization ratios:
 *   <70%  → green
 *   70-99 → amber
 *   100%  → red
 *
 * Clicking opens a popover with the Sales / Exec breakdown (progress bars) and a
 * link to the management page (/admin/seats).
 */

function pickColor(maxRatio: number): string {
  if (maxRatio >= 1) return '#EF4444'; // red
  if (maxRatio >= 0.7) return '#F59E0B'; // amber
  return '#10B981'; // green
}

const SeatQuotaBadge: React.FC = () => {
  const { tenantId } = useAccountClaims();
  const navigate = useNavigate();

  const isTenantAdmin = useHasAdminAccess();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'seats-summary', tenantId],
    queryFn: () => accountsApi.getSeatsSummary(tenantId as string),
    enabled: isTenantAdmin && !!tenantId,
    staleTime: 60_000,
    retry: false,
  });

  if (!isTenantAdmin) return null;
  if (isLoading) return <Spin size="small" />;
  if (isError || !data) return null;

  const salesRatio =
    data.total_sales_seats > 0 ? data.used_sales_seats / data.total_sales_seats : 0;
  const execRatio =
    data.total_executive_seats > 0
      ? data.used_executive_seats / data.total_executive_seats
      : 0;
  const color = pickColor(Math.max(salesRatio, execRatio));

  const renderSeatLine = (
    label: string,
    used: number,
    total: number,
    ratio: number,
  ) => (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <Text style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>{label}</Text>
        <Text style={{ fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 600 }}>
          {used}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {' '}
            / {total}
          </Text>
        </Text>
      </div>
      <Progress
        percent={total > 0 ? Math.round(ratio * 100) : 0}
        showInfo={false}
        strokeColor={pickColor(ratio)}
        trailColor="#F1F5F9"
        size="small"
        style={{ marginBottom: 0 }}
        aria-label={`${label}: ${used} de ${total}`}
      />
    </div>
  );

  const content = (
    <div style={{ width: 264 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <Text strong style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>
          Cadeiras
        </Text>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
          Plano {data.plan_slug}
        </Text>
      </div>

      {renderSeatLine('Vendas', data.used_sales_seats, data.total_sales_seats, salesRatio)}
      {renderSeatLine(
        'Executivo',
        data.used_executive_seats,
        data.total_executive_seats,
        execRatio,
      )}

      {data.data_partial && (
        <Text style={{ display: 'block', fontSize: 11, color: '#F59E0B', marginTop: 4 }}>
          Dados parciais — alguns valores podem não estar atualizados.
        </Text>
      )}

      <Divider style={{ margin: '12px 0 8px' }} />
      <a
        onClick={() => navigate('/admin/seats')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          fontSize: 12,
          color: '#6366F1',
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        Gerenciar cadeiras
        <ArrowUpRight size={12} />
      </a>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ borderRadius: 12, padding: 16 }}
    >
      <button
        aria-label="Cadeiras do plano"
        className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
        style={{ color }}
      >
        <Users size={18} />
      </button>
    </Popover>
  );
};

export default SeatQuotaBadge;
